import React, { FC, useState } from 'react'
import {
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Button,
  Divider,
  Tooltip,
} from '@mui/material'
import MedicationIcon from '@mui/icons-material/Medication'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

// UI定数
const POPOVER_WIDTH = 350
const POPOVER_MAX_WIDTH = '90vw'
const MAX_LIST_HEIGHT = 300

interface MedicationInsertButtonProps {
  medications: string[] // 利用可能な医薬品リスト
  onInsert: (text: string) => void // 挿入時のコールバック
  buttonSize?: 'small' | 'medium'
  disabled?: boolean
  tooltip?: string
}

export const MedicationInsertButton: FC<MedicationInsertButtonProps> = ({
  medications,
  onInsert,
  buttonSize = 'small',
  disabled = false,
  tooltip = '医薬品を挿入',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selected, setSelected] = useState<string[]>([])

  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSelected([]) // 閉じる時に選択をリセット
  }

  const toggleSelection = (medication: string) => {
    setSelected((prev) => {
      if (prev.includes(medication)) {
        return prev.filter((m) => m !== medication)
      } else {
        return [...prev, medication]
      }
    })
  }

  const handleInsert = () => {
    if (selected.length === 0) return

    // 複数選択時は「、」で結合
    const textToInsert = selected.join('、')
    onInsert(textToInsert)
    handleClose()
  }

  const handleSelectAll = () => {
    if (selected.length === medications.length) {
      setSelected([])
    } else {
      setSelected([...medications])
    }
  }

  return (
    <>
      <Tooltip title={tooltip} placement="top">
        <Box component="span" sx={{ display: 'inline-block' }}>
          <IconButton
            onClick={handleOpen}
            size={buttonSize}
            disabled={disabled || medications.length === 0}
            sx={{
              color:
                disabled || medications.length === 0
                  ? 'action.disabled'
                  : colorTokens.text.secondary,
              '&:hover': {
                backgroundColor: 'action.hover',
                color: colorTokens.blue[600],
              },
            }}
          >
            <MedicationIcon
              fontSize={buttonSize === 'small' ? 'small' : 'medium'}
            />
          </IconButton>
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ width: POPOVER_WIDTH, maxWidth: POPOVER_MAX_WIDTH }}>
          {/* ヘッダー */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <MedicationIcon
                fontSize="small"
                sx={{ color: colorTokens.text.secondary }}
              />
              医薬品を挿入
            </Typography>
            {medications.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                複数選択時は「、」で結合されます
              </Typography>
            )}
          </Box>

          <Divider />

          {medications.length === 0 ? (
            // 医薬品がない場合
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                処方薬情報がありません
              </Typography>
            </Box>
          ) : (
            <>
              {/* 全選択ボタン */}
              {medications.length > 1 && (
                <Box sx={{ px: 2, py: 1 }}>
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    sx={{
                      color: colorTokens.text.secondary,
                      '&:hover': {
                        backgroundColor: 'rgba(117, 117, 117, 0.08)',
                        color: colorTokens.grey[700],
                      },
                    }}
                  >
                    {selected.length === medications.length
                      ? '全て解除'
                      : '全て選択'}
                  </Button>
                </Box>
              )}

              {/* 医薬品リスト */}
              <List
                sx={{
                  maxHeight: MAX_LIST_HEIGHT,
                  overflow: 'auto',
                  py: 0,
                }}
              >
                {medications.map((medication) => {
                  const isSelected = selected.includes(medication)
                  return (
                    <ListItem key={medication} disablePadding>
                      <ListItemButton
                        onClick={() => toggleSelection(medication)}
                        dense
                        sx={[
                          {
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          },
                          isSelected
                            ? {
                                backgroundColor: 'action.selected',
                              }
                            : {
                                backgroundColor: 'transparent',
                              },
                        ]}
                      >
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          sx={{
                            color: 'action.active',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                        <ListItemText
                          primary={medication}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: {
                              fontWeight: isSelected ? 500 : 400,
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>

              <Divider />

              {/* フッター */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  選択: {selected.length}件
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={handleClose}
                    size="small"
                    sx={{
                      color: colorTokens.text.secondary,
                      '&:hover': {
                        backgroundColor: 'rgba(117, 117, 117, 0.08)',
                        color: colorTokens.grey[700],
                      },
                    }}
                  >
                    キャンセル
                  </Button>
                  <Button
                    onClick={handleInsert}
                    variant="contained"
                    size="small"
                    disabled={selected.length === 0}
                  >
                    挿入
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}
